<template>
  <div class="user-mgmt-wrapper">
    <!-- Dialog Components -->

    <!-- Organization Alert Dialog -->
    <div v-if="showOrgAlert" class="dialog-overlay">
      <div class="dialog-box">
        <h3>Missing Information</h3>
        <p>You must select an organization for each user.</p>
        <div class="dialog-actions">
          <button @click="closeOrgAlert">OK</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <div v-if="showDeleteConfirm" class="dialog-overlay">
      <div class="dialog-box">
        <h3>Delete Confirmation</h3>
        <p>Are you sure you want to delete this user?</p>
        <div class="dialog-actions">
          <button @click="deleteUserConfirmed" class="button-primary">OK</button>
          <button @click="closeDeleteConfirm" class="button-secondary">
            <span>
              Cancel
            </span>
          </button>
        </div>
      </div>
    </div>
    <div class="container">
      <Title>User Management</Title>
      <div class="card-section">
        <h3>Add New User</h3>
        <div class="user-form">
          <div class="form-grid">
            <div>
              <label for="new-username">Username:</label>
              <input type="text"
                     id="new-username"
                     v-model="newUser.username"
                     @input="hideUsernameError"
              />
              <div v-if="showUsernameError" class="error-message">Please select a different username</div>
              <p v-if="showMissingUsernameError" class="input-error">Enter a username.</p>
            </div>
            <div>
              <label for="new-password">Password:</label>
              <input type="password" id="new-password" v-model="newUser.password" @input="hidePasswordError"/>
              <p v-if="showMissingPasswordError" class="input-error">Enter a password.</p>
            </div>
            <div>
              <label for="new-organization">Organization:</label>
              <select id="new-organization" v-model="newUser.org_id" @click="hideOrganizationError">
                <option value="" disabled selected>Select Organization</option>
                <option v-for="org in filteredOrganizations" :key="org.id" :value="org.id">{{ org.name }}</option>
              </select>
              <p v-if="showMissingOrganizationError" class="input-error">Select an organization.</p>
            </div>
            <div>
              <label for="new-role">Role:</label>
              <select id="new-role" v-model="newUser.role_ids" multiple @select="hideRolesError">
                <option
                    v-for="role in filteredRoles"
                    :key="role.id"
                    :value="role.id"
                >
                  {{ role.role_name }}
                </option>
              </select>
              <p v-if="showMissingRolesError" class="input-error">Select at least one role.</p>
            </div>
          </div>
          <button @click.prevent="addUser" class="button-primary">Add User</button>
        </div>
      </div>

      <div class="user-table">
        <table>
          <thead>
          <tr>
            <th @click="sort('id')">ID</th>
            <th @click="sort('username')">Username</th>
            <th @click="sort('organization')">Organization</th>
            <th @click="sort('role')">Role</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="user in sortedUsers" :key="user.id">
            <td>{{ user.id }}</td>
            <td>
              <input v-if="isEditing && editingUser.id === user.id" v-model="editingUser.username"
                     class="table-input"/>
              <span v-else>{{ user.username }}</span>
            </td>


            <td>
              <select v-if="isEditing && editingUser.id === user.id && isAdmin" v-model="editingUser.org_id"
                      class="table-input">
                <option v-for="org in organizations" :key="org.id" :value="org.id">{{ org.name }}</option>
              </select>
              <span v-else>{{ getOrganizationName(user.org_id) }}</span>
            </td>


            <td>
              <select v-if="isEditing && editingUser.id === user.id && isAdmin" v-model="editingUser.role_ids"
                      class="table-input" multiple>
                <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.role_name }}</option>
              </select>
              <span v-else v-for="role in user.roles">
                  {{ role.role_name }} &nbsp
                </span>
            </td>


            <td>
              <button v-if="isEditing && editingUser.id === user.id" class="action-button save-button"
                      @click="updateUser">Save
              </button>
              <button v-else class="action-button edit-button" @click="editUser(user)">Edit</button>

              <button v-if="isEditing && editingUser.id === user.id" class="action-button cancel-button"
                      @click="cancelEdit">Cancel
              </button>
              <button v-else class="action-button delete-button" @click="confirmDeleteUser(user)">Delete</button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, reactive, computed, onMounted} from 'vue'
import apiService from '@/services/apiService'
import Title from '../common/Title.vue'

defineEmits(['login', 'logout'])

const users = ref([]);
const organizations = ref([]);
const roles = ref([]);

const isEditing = ref(false);
const editingUser = ref({});
const showMissingUsernameError = ref(false);
const showMissingPasswordError = ref(false);
const showMissingOrganizationError = ref(false);
const showMissingRolesError = ref(false);

const newUser = reactive({
  username: '',
  password: '',
  org_id: '',
  role_ids: []
})

const currentSort = ref('id')
const currentSortDir = ref('asc')

const showOrgAlert = ref(false)
const showDeleteConfirm = ref(false)
const userToDelete = ref(null)

const showUsernameError = ref(false)

const isAdmin = computed(() => {
  const roles = JSON.parse(localStorage.getItem('roles') || '[]')
  return roles.includes('ADMIN')
})

const filteredRoles = computed(() => {
  if (isAdmin.value) return roles.value
})

const filteredOrganizations = computed(() => {
  if (isAdmin.value) return organizations.value

  const userOrgId =
      parseInt(localStorage.getItem('org_id')) || 0

  return organizations.value.filter(
      org => org.id === userOrgId
  )
})

const sortedUsers = computed(() => {
  let usersToShow = [...users.value]

  return usersToShow.sort((a, b) => {
    let modifier =
        currentSortDir.value === 'desc'
            ? -1
            : 1

    if (a[currentSort.value] < b[currentSort.value])
      return -1 * modifier

    if (a[currentSort.value] > b[currentSort.value])
      return 1 * modifier

    return 0
  })
})

onMounted(async () => {
  try {
    await Promise.all([
      loadUsers(),
      loadOrganizations(),
      loadRoles()
    ])

    if (!isAdmin.value) {
      const userOrgId =
          parseInt(localStorage.getItem('org_id')) || 0

      const userOrg =
          organizations.value.find(
              org => org.id === userOrgId
          )

      if (userOrg) {
        newUser.org_id = userOrg.id
      }

    }
  } catch (error) {
    console.error(error)
  }
})


async function loadUsers() {
  try {
    users.value =
        await apiService.fetchUsers()
  } catch (error) {
    console.error(error)
  }
}

async function loadOrganizations() {
  try {
    organizations.value =
        await apiService.getOrganizations()
  } catch (error) {
    console.error(error)
  }
}

async function loadRoles() {
  try {
    const response =
        await apiService.fetchRoles()

    roles.value = response.roles

  } catch (error) {
    console.error(error)
  }
}

function sort(column) {
  if (currentSort.value === column) {
    currentSortDir.value =
        currentSortDir.value === 'asc'
            ? 'desc'
            : 'asc'
  } else {
    currentSort.value = column
    currentSortDir.value = 'asc'
  }
}

function editUser(user) {
  isEditing.value = true
  editingUser.value = {...user}
}

function cancelEdit() {
  isEditing.value = false
  editingUser.value = {}
}

async function updateUser() {
  try {
    if (
        editingUser.value.org_id == null
    ) {
      showOrgAlert.value = true
      return
    }

    await apiService.updateUser(
        editingUser.value.id,
        {
          username:
          editingUser.value.username,
          role_ids:
          editingUser.value.role_ids,
          org_id:
          editingUser.value.org_id
        }
    )

    await loadUsers()
    cancelEdit()
  } catch (error) {
    console.error(error)
  }
}

async function addUser() {
  const validInputs = checkNewUserInputs();
  if (!validInputs) {
    return;
  }

  try {
    const existingUser =
        users.value.find(
            user =>
                user.username ===
                newUser.username
        )

    if (existingUser) {
      showUsernameError.value = true

      setTimeout(() => {
        showUsernameError.value = false
      }, 3000)

      return
    }

    if (newUser.org_id === '') {
      showOrgAlert.value = true
      return
    }

    await apiService.createUser(newUser)

    await loadUsers()

    newUser.username = ''
    newUser.password = ''
    newUser.org_id = ''
    newUser.role_ids = [
      roles.value[0]?.id
    ]

  } catch (error) {
    console.error(error)
  }
}

function confirmDeleteUser(user) {
  userToDelete.value = user
  showDeleteConfirm.value = true
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  userToDelete.value = null
}

async function deleteUserConfirmed() {
  try {
    await apiService.deleteUser(
        userToDelete.value.id
    )

    await loadUsers()

    closeDeleteConfirm()
  } catch (error) {
    console.error(error)
  }
}

function closeOrgAlert() {
  showOrgAlert.value = false
}

function getOrganizationName(orgId) {
  const org =
      organizations.value.find(
          o => o.id === orgId
      )

  return org?.name || 'Unknown'
}

function checkNewUserInputs() {
  if (newUser.username.trim() === "") {
    showMissingUsernameError.value = true;
    return false;
  }

  if (newUser.password.trim() === "") {
    showMissingPasswordError.value = true;
    return false;
  }

  if (newUser.org_id === ""){
    showMissingOrganizationError.value = true;
    return false;
  }

  if (newUser.role_ids.length === 0) {
    showMissingRolesError.value = true;
    return false;
  }

  return true;
}

function hideUsernameError() {
  showMissingUsernameError.value = false;
}

function hidePasswordError() {
  showMissingPasswordError.value = false;
}

function hideOrganizationError() {
  showMissingOrganizationError.value = false;
}

function hideRolesError() {
  showMissingRolesError.value = false;
}
</script>


<style scoped>
.user-mgmt-wrapper {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #323232;
}

.card-section {
  background-color: white;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
}

h1 {
  color: #323232;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid #13294B;
  padding-bottom: 0.5rem;
  display: inline-block;
}

h2,
h3 {
  color: #323232;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.user-form {
  background-color: white;
  border-radius: 6px;
  border: 0.5px solid #13294B;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.user-form h2 {
  color: #323232;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.user-form label {
  display: block;
  color: #323232;
  font-size: 14px;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.user-form input,
.user-form select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  margin-bottom: 1rem;
  box-sizing: border-box;
}

.user-form input:focus,
.user-form select:focus {
  outline: none;
  border-color: #13294B;
}

.input-error {
  border: 1px solid #f8d7da !important;
}

.error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: -0.8rem;
  margin-bottom: 1rem;
}


.user-table {
  margin-bottom: 1.5rem;
  background-color: white;
  border-radius: 6px;
  padding: 1.5rem;
}

.user-table table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-size: 14px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e8e9f5;
}

.user-table th,
.user-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e8e9f5;
  font-weight: 500;
}

.user-table th {
  background-color: #d4def3;
  font-weight: 700;
  color: #323232;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.user-table tr:last-child td {
  border-bottom: none;
}

.user-table tr:hover {
  background-color: #f9f9f9;
}

.action-button,
.edit-button,
.delete-button {
  min-width: 80px;
  height: 32px;
  padding: 0 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: 1px solid black;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 5px;
}

.edit-button {
  background-color: #d3d3d3;
}

.edit-button:hover {
  background-color: #e8e8e8;
}

.delete-button {
  background-color: #e64141;
}

.delete-button:hover {
  background-color: #f5c6cb;
}


.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.dialog-box {
  background-color: white;
  padding: 1.5rem;
  border-radius: 6px;
  width: 350px;
  border: 1px solid #e8e9f5;
}

.dialog-box h3 {
  margin-top: 0;
  color: #323232;
}

.dialog-box p {
  margin-bottom: 1rem;
  color: #323232;
}

.dialog-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 1rem;
}

@media (min-width: 768px) {
  .user-form .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

.table-input {
  width: 100%;
  max-width: 150px;
  padding: 6px 8px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  height: 32px;
}

.table-input:focus {
  outline: none;
  border-color: #505050;
}

.save-button {
  background-color: #2a5ca8;
  color: white;
}

.save-button:hover {
  background-color: #1a3968;
}

.cancel-button {
  background-color: #e8e8e8;
  color: #323232;
}

.cancel-button:hover {
  background-color: #d8d8d8;
}

.user-table .action-button {
  min-width: 60px;
  margin-right: 4px;
}

.user-table .reset-password-button {
  min-width: 60px;
}

.readonly-input {
  background-color: #f8f9fa !important;
  color: #6c757d;
  cursor: not-allowed;
}
</style>
